<template>
  <b-navbar
    type="dark"
    variant="info"
    :sticky="true"
    :class="navbarClass"
    :topic="!!topicId ? topicId : false"
  >
    <template v-if="search == false">
      <nav-bar-left
        :user="user"
        :back="back"
        @on-back="doBack"
        :domainId="domainId"
        :domains="domains"
        :cancel="cancel"
        @on-cancel="$emit('on-cancel')"
        :left-items="leftItems"
      />

      <b-navbar-nav class="mx-auto" :visible="0 < title.length">
        <b-nav-text class="text-center">{{ title }}{{ subTitle }}</b-nav-text>
      </b-navbar-nav>

      <nav-bar-right
        class="mr-0"
        :user="user"
        :ok="ok"
        @on-ok="$emit('on-ok')"
        :complete="complete"
        @on-complete="$emit('on-complete')"
        :close="close"
        @on-close="$emit('on-close')"
        :appClose="appClose"
        @on-app-close="doAppClose"
        :preset="preset"
        @on-menu-open="doMenuOpen"
        :items="items"
        :domainId="domainId"
      />
    </template>
    <template v-else>
      <b-navbar class="navbar1st py-0 pl-2 pr-2">
        <nav-bar-left
          :user="user"
          :back="back"
          @on-back="doBack"
          :domainId="domainId"
          :brand="brand"
          :domains="domains"
          :cancel="cancel"
          @on-cancel="$emit('on-cancel')"
          :left-items="leftItems"
        />

        <!-- 旧ナビゲーションバー
                <b-navbar-nav v-if="apps" pills fill class="mx-3 directapp-selector h-100">
                    <b-nav-item v-if="appEnable('talk')"      :href="talkUrl"     target="direct-home" class="h-100 d-flex align-items-center nav-item-grid-2">トーク</b-nav-item>
                    <b-nav-item v-if="appEnable('task')"      :href="taskUrl"     target="direct-apps" class="h-100 d-flex align-items-center nav-item-grid-2">タスク</b-nav-item>
                    <b-nav-item v-if="appEnable('schedule')"  :href="scheduleUrl" target="direct-apps" class="h-100 d-flex align-items-center nav-item-grid-3">スケジュール</b-nav-item>
                    <b-nav-item v-if="appEnable('forum')"                                              class="h-100 d-flex align-items-center nav-item-grid-2 active">掲示板</b-nav-item>
                </b-navbar-nav>
                -->

        <b-navbar-nav
          class="mx-auto nav-title flex-grow-1 justify-content-center"
          :visible="0 < title.length"
        >
          <b-nav-text class="text-center">{{ title }}{{ subTitle }}</b-nav-text>
        </b-navbar-nav>

        <nav-bar-right
          class="ml-auto mr-0"
          :user="user"
          :ok="ok"
          @on-ok="$emit('on-ok')"
          :complete="complete"
          @on-complete="$emit('on-complete')"
          :close="close"
          @on-close="$emit('on-close')"
          :appClose="appClose"
          @on-app-close="doAppClose"
          :preset="preset"
          @on-menu-open="doMenuOpen"
          :items="items"
          :domainId="domainId"
          :solution-links="solutionItems"
        />
      </b-navbar>
      <b-navbar class="navbar2nd d-md-none d-lg-none d-xl-none">
        <search-form ref="search" />
      </b-navbar>
    </template>
  </b-navbar>
</template>
<style scoped></style>
<script lang="ts">
import {
  computed,
  defineComponent,
  getCurrentInstance,
  onMounted,
  onUpdated,
  ref,
  type PropType,
} from "vue";
import type Vue from "vue";
import { useUserStore } from "@/store";
import NavBarLeft from "./navigation-bar/NavBarLeft.vue";
import NavBarRight from "./navigation-bar/NavBarRight.vue";
import SearchForm from "./SearchForm.vue";
import { directAppUrl, AppKeys, appAvailable } from "../direct-app-config";
import "./navigation-bar.scss";
import HistoryUtility from "../history";
import { AppLinkItem, getSolutionItems } from "./solutions/";
import { User } from "@/model";
import { useLegacyRootEvent } from "@/composables/useLegacyRootEvent";

export type NavigationItem = { to: string; label: string; click?: string };

/**
 * ナビゲーションバー
 *
 * ■左側
 * back       戻るボタン
 * domains    組織選択ドロップダウンボタン
 * cancel     キャンセルボタン
 * left-items 指定されたものを表示
 *
 * ■右側
 * ok         OKボタン
 * complete   完了ボタン
 * items      指定メニュー
 * preset     プリセットメニュー（ユーザーアイコンから始まるメニュー）
 * close      ✕ボタン
 *
 */
export default defineComponent({
  name: "navigation-bar",
  // NOTE: 旧コードでは ProfileIcon も登録されていたが、テンプレート未使用の
  // デッド登録（vue/no-unused-components エラー）だったため除去（振る舞い不変）。
  components: { NavBarLeft, NavBarRight, SearchForm },
  props: {
    title: { type: String, default: () => "" }, // タイトル
    subTitle: { type: String, default: () => "" }, // サブタイトル
    domainId: { type: String, default: () => "" }, // 選択されたdomainId
    apps: { type: Boolean, default: () => false }, // true: directアプリへのリンクを出す
    back: { type: Boolean, default: () => false }, // true: 組織選択ドロップダウン
    brand: { type: Boolean, default: () => false }, // true: ブランドアイコン表示
    domains: { type: Boolean, default: () => false }, // true: 組織選択ドロップダウン
    cancel: { type: Boolean, default: () => false }, // true: キャンセルボタン
    /** ナビゲーションの左側メニューアイテム。２つ以上でドロップダウンになります */
    leftItems: { type: Array as PropType<NavigationItem[]>, default: () => [] },
    /** ナビゲーションの右側メニューアイテム */
    ok: { type: Boolean, default: () => false },
    complete: { type: Boolean, default: () => false },
    items: { type: Array as PropType<NavigationItem[]>, default: () => [] },
    preset: { type: Boolean, default: () => false },
    close: { type: Boolean, default: () => false },
    appClose: { type: Boolean, default: () => false }, //!< アプリを閉じるボタン(iOS/Androidで利用)
    search: { type: Boolean, default: () => false },
    searching: { type: Boolean, default: () => false }, //!< true: 検索中
    // 外部から設定可能にする
    userSpecified: { type: Object as PropType<User>, default: undefined },
  },
  setup(props, { emit }) {
    // vue-router 3.5 には useRouter/useRoute が無いため $router/$route をインスタンス経由で取得
    const vm = getCurrentInstance()?.proxy as Vue | undefined;
    const rootEvent = useLegacyRootEvent();

    const user = ref<User>(User.createNotFoundUser());

    const isInvalidDomainId = ref<boolean>(false);

    const leftEmpty = computed((): boolean => {
      return (
        props.back == false &&
        props.domains == false &&
        props.cancel == false &&
        props.leftItems.length == 0
      );
    });
    const empty = computed((): boolean => {
      return (
        props.ok == false &&
        props.complete == false &&
        props.items.length == 0 &&
        props.preset == false &&
        props.close == false &&
        props.search == false
      );
    });

    // ナビゲーションバーの基礎class
    const navbarClass = computed((): string => {
      if (props.search) {
        return "navbar-base-search";
      } else {
        return "navbar-base";
      }
    });

    const userId = computed((): string => user.value.directId);
    const userName = computed((): string => user.value.name);
    const userImage = computed((): string => user.value.getProfileIconUrl());

    const topicId = computed((): string | undefined => {
      if (!vm?.$route) return undefined;
      const topicId = vm.$route.params["topicId"];
      return topicId;
    });

    // ソリューション一覧
    const solutionItems = computed((): AppLinkItem[] => getSolutionItems(props.domainId));

    // direct、タスク、スケジュールへのリンク
    const talkUrl = computed((): string => directAppUrl(AppKeys.TALK, props.domainId));
    const taskUrl = computed((): string => directAppUrl(AppKeys.TASK, props.domainId));
    const scheduleUrl = computed((): string => directAppUrl(AppKeys.SCHEDULE, props.domainId));

    // 各アプリが有効かどうか
    // appEnable( app: AppKeys ): boolean { return appAvailable(app) }
    const appEnable = (app: AppKeys): boolean => {
      return appAvailable(app);
    };

    onUpdated((): void => {
      // ユーザー情報が外部から設定される
      if (props.userSpecified) {
        user.value = props.userSpecified;
        return;
      }

      const me = useUserStore().me;
      user.value = me;
    });

    onMounted((): void => {
      rootEvent.on("on-overlay-with-search-window", (isInvalid: boolean) => {
        isInvalidDomainId.value = isInvalid;
      });
    });

    const doAppClose = (): void => {
      rootEvent.emit("on-app-close");
    };

    /** 戻る */
    const doBack = (): void => {
      const router = vm?.$router;
      if (router) {
        if (HistoryUtility.isTopHistory()) {
          const path = router.currentRoute.path;
          if (path == "/login-error-oidc") {
            doAppClose();
            return;
          }
          const name = router.currentRoute.name;
          switch (name) {
            case "message-list": // 現在投稿一覧にいる場合は、話題一覧に遷移する
              router.replace(`/${props.domainId}`);
              break;
            case "topic-list":
              doAppClose(); // 現在話題一覧にいる場合は、アプリを閉じる(話題一覧ヘッダーにbackボタンがあるのはモバイルのみ)
              break;
            default: // その他の場合、ヒストリーに沿う
              router.go(-1); // go(-1)がOIDC認可画面でも判定する方法は存在しないためブロックできない
              break;
          }
        } else {
          router.go(-1); // 履歴を戻すだけ
        }
      }
    };

    /** キャンセル */
    const doCancel = (): void => {
      emit("on-cancel");
    };

    /** 閉じる */
    const doClose = (): void => {
      emit("on-close");
    };

    /** OK */
    const doOk = (): void => {
      emit("on-ok");
    };

    /** 完了 */
    const doComplete = (): void => {
      emit("on-complete");
    };

    const doMenuOpen = (): void => {
      const selections = [
        {
          message: "コメントの並び順設定",
          action: () => {
            rootEvent.emit("show-sort-comment-setting");
          },
        },
        {
          message: "同期",
          action: () => {
            rootEvent.emit("sync-data-alert");
          },
        },
      ];
      rootEvent.emit("open-action-sheet", selections);
    };

    return {
      user,
      isInvalidDomainId,
      leftEmpty,
      empty,
      navbarClass,
      userId,
      userName,
      userImage,
      topicId,
      solutionItems,
      talkUrl,
      taskUrl,
      scheduleUrl,
      appEnable,
      doBack,
      doCancel,
      doClose,
      doOk,
      doComplete,
      doAppClose,
      doMenuOpen,
    };
  },
});
</script>
